import React from 'react';
import { CheckCircleIcon, FileIcon } from '../icons/ChatCategoryIcons.js';
import FieldHelpTooltip from '../common/FieldHelpTooltip';
import { SUPPORTED_UPLOAD_ACCEPT } from '../../constants/fileTypes';

const ClubHelpLabel = ({ children, title, items }) => (
  <span className="sgb-label-with-help">
    {children}
    <FieldHelpTooltip title={title} items={items} ariaLabel={`${children} 안내`} />
  </span>
);

const CLUB_HELP = {
  studentFiles: {
    title: '학생 활동 파일 예시',
    items: [
      '학생 활동 보고서, 포트폴리오, 발표 자료, 사진 기록처럼 학생이 실제로 한 활동이 보이는 파일을 올려주세요.',
      '계획서보다 학생별 역할과 결과물이 있는 자료를 더 중요하게 반영해요.',
    ],
  },
  clubInfo: {
    title: '동아리 정보에는 무엇을 쓰나요?',
    items: [
      '동아리명, 활동 목표, 주요 활동, 학생 역할, 활동 주제를 적어주세요.',
      '계획서는 방향을 잡는 자료이고, 학생 활동 내용이 있으면 더 구체적으로 써져요.',
    ],
  },
  planFile: {
    title: '동아리 계획서 파일 예시',
    items: [
      '연간 동아리 계획서, 운영 계획, 활동 주제 목록, 지도 계획처럼 동아리의 방향이 담긴 파일을 올려주세요.',
      '학생별 활동 보고서가 있다면 활동 파일에도 함께 올려주세요.',
    ],
  },
};

/**
 * 동아리 카테고리 전용 입력 폼
 * - 학생 활동 내용 (텍스트 + 파일 첨부)
 * - 동아리 정보/계획서 (텍스트 + AI 자동 분석 파일)
 */
const ClubForm = ({
  clubData,
  setClubData,
  fileUpload,
  isDragging,
  isProUser,
  auth,
  handleFileUpload,
  handleFileRemove,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleClubStudentFilesUpload,
  removeClubStudentFile,
  setShowLoginPrompt,
  setShowProModal,
  selectedCategory,
  rosterPickLine = null,
}) => {
  return (
    <div style={{ marginBottom: '15px' }}>
      {/* 동아리 계획서 파일 input (AI 자동 분석) */}
      <input
        type="file"
        id="club-plan-file-input"
        accept={SUPPORTED_UPLOAD_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            handleFileUpload(file);
          }
          e.target.value = '';
        }}
      />
      
      {/* 학생 활동 파일 input (다중) */}
      <input
        type="file"
        id="club-student-files-input"
        accept={SUPPORTED_UPLOAD_ACCEPT}
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          const newFiles = Array.from(e.target.files);
          if (newFiles.length > 0) {
            handleClubStudentFilesUpload(newFiles);
          }
          e.target.value = '';
        }}
      />
      
      {/* 1. 학생 활동 내용 (텍스트 입력 + 파일 첨부) */}
      <div
        className="saenggibu-inline-file-drop-target"
        data-saenggibu-drop-target="student-activity"
        data-saenggibu-drop-label="동아리 활동 파일을 여기에 놓아주세요"
        style={{ marginBottom: '16px' }}
      >
        <p style={{ 
          fontSize: '14px', 
          color: 'var(--whatquiz-gray-800)', 
          marginBottom: '8px',
          fontWeight: '600'
        }}>
          학생 활동 내용
        </p>
        
        <textarea
          value={clubData.studentActivity}
          onChange={(e) => setClubData(prev => ({ ...prev, studentActivity: e.target.value }))}
          placeholder="학생 활동, 역할, 특성 입력"
          style={{
            width: '100%',
            minHeight: '80px',
            padding: '12px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            resize: 'vertical',
            fontFamily: 'inherit',
            lineHeight: '1.5',
            boxSizing: 'border-box'
          }}
        />
        
        {/* 파일 첨부 (한 줄) */}
        <div 
          onClick={() => {
            if (!auth.currentUser) {
              setShowLoginPrompt(true);
              return;
            }
            if (!isProUser) {
              setShowProModal(true);
              return;
            }
            document.getElementById('club-student-files-input').click();
          }}
          style={{
            marginTop: '6px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            backgroundColor: 'transparent',
            borderRadius: '6px',
            border: '1px dashed #d1d5db',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            color: '#3182f6',
            fontSize: '13px',
            fontWeight: '500'
          }}
        >
          <span className="sgb-upload-row">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
            <span>
              {clubData.studentFiles.length > 0 
                ? `${clubData.studentFiles.length}개 파일 첨부됨` 
                : '활동 파일 첨부'}
            </span>
            <FieldHelpTooltip title={CLUB_HELP.studentFiles.title} items={CLUB_HELP.studentFiles.items} ariaLabel="동아리 활동 파일 안내" />
          </span>
        </div>
        {rosterPickLine}
        
        {/* 업로드된 파일 목록 */}
        {clubData.studentFiles.length > 0 && (
          <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {clubData.studentFiles.map((file, index) => (
              <div key={index} style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 8px',
                backgroundColor: '#fff',
                borderRadius: '4px',
                border: '1px solid #e5e7eb',
                fontSize: '12px'
              }}>
                <span style={{ color: 'var(--whatquiz-gray-800)', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {file.name}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeClubStudentFile(index);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--whatquiz-error)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    padding: '8px',
                    minWidth: '36px',
                    minHeight: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1
                  }}
                  aria-label="삭제"
                >×</button>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* 2. 동아리 정보(계획서) 입력 */}
      <div
        className="saenggibu-inline-file-drop-target"
        data-saenggibu-drop-target="plan"
        data-saenggibu-drop-label="동아리 계획서를 여기에 놓아주세요"
        style={{ marginBottom: '12px' }}
      >
        <p style={{ 
          fontSize: '14px', 
          color: 'var(--whatquiz-gray-800)', 
          marginBottom: '8px',
          fontWeight: '600'
        }}>
          <ClubHelpLabel title={CLUB_HELP.clubInfo.title} items={CLUB_HELP.clubInfo.items}>동아리 정보(계획서)</ClubHelpLabel>
        </p>
        <textarea
          value={clubData.clubDescription}
          onChange={(e) => setClubData(prev => ({ ...prev, clubDescription: e.target.value }))}
          placeholder="동아리명과 활동 내용"
          style={{
            width: '100%',
            minHeight: '100px',
            padding: '12px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '14px',
            resize: 'vertical',
            fontFamily: 'inherit',
            lineHeight: '1.5',
            boxSizing: 'border-box'
          }}
        />
        
        {/* 파일 첨부 - AI 자동 분석 */}
        {fileUpload.uploading && selectedCategory === '동아리' ? (
          <div style={{
            marginTop: '8px',
            padding: '8px 12px',
            backgroundColor: 'var(--whatquiz-yellow-50)',
            border: '1px solid var(--whatquiz-warning)',
            borderRadius: '6px',
            fontSize: '13px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileIcon size={16} color="#ff9800" />
              <span style={{ color: '#e65100', flex: 1 }}>
                {fileUpload.file?.name || '파일'}
              </span>
              <span style={{
                backgroundColor: 'var(--whatquiz-warning)',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span style={{
                  width: '10px',
                  height: '10px',
                  border: '2px solid white',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  display: 'inline-block'
                }} />
                읽는 중
              </span>
            </div>
          </div>
        ) : !fileUpload.file && !clubData.planFile ? (
          <div 
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => {
              if (!auth.currentUser) {
                setShowLoginPrompt(true);
                return;
              }
              if (!isProUser) {
                setShowProModal(true);
                return;
              }
              document.getElementById('club-plan-file-input').click();
            }}
            style={{
              marginTop: '6px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              backgroundColor: 'transparent',
              borderRadius: '6px',
              border: isDragging ? '1.5px dashed #3182f6' : '1px dashed #d1d5db',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              color: '#3182f6',
              fontSize: '13px',
              fontWeight: '500'
            }}
          >
            <span className="sgb-upload-row">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>
              <b>동아리 계획서</b> 첨부
              <FieldHelpTooltip title={CLUB_HELP.planFile.title} items={CLUB_HELP.planFile.items} ariaLabel="동아리 계획서 파일 안내" />
            </span>
          </div>
        ) : (fileUpload.file || clubData.planFile) && (
          <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 8px',
              backgroundColor: '#fff',
              borderRadius: '4px',
              border: '1px solid #e5e7eb',
              fontSize: '12px'
            }}>
              <span style={{ color: 'var(--whatquiz-gray-800)', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <CheckCircleIcon size={14} color="currentColor" style={{ verticalAlign: 'middle', marginRight: '3px' }} /> {fileUpload.file?.name || clubData.planFile?.name || clubData.planFile}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setClubData(prev => ({ ...prev, planFile: null, planFileContent: null, clubDescription: '' }));
                  handleFileRemove();
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--whatquiz-error)',
                  cursor: 'pointer',
                  fontSize: '14px',
                  padding: '0',
                  lineHeight: 1
                }}
              >×</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClubForm;
